/**
 * French street, business and landmark names. Owned by `world`.
 *
 * `landmarkNear()` feeds these straight onto the UI destination cards, so they have to read
 * like real Plateau/Mile End addresses, not like placeholder strings.
 */

/**
 * North-south streets, west → east. Index-matched to TUNING.grid.nsX.
 *
 * The first three are the phase-1 downtown additions and are PREPENDED for the same reason
 * their coordinates were: `nsX` must stay ascending, so a westward extension shifts every
 * later index by three. Keep this array and `TUNING.grid.nsX` edited together or every street
 * blade in the city points at the wrong road.
 *
 * Order is the real west→east order downtown (Atwater, Guy, Peel), and `rue Guy` carries the
 * artère because it genuinely is the wide one of the three.
 */
export const NS_NAMES = [
  'ch. de la Côte-des-Neiges',
  'voie Camillien-Houde',
  'av. du Parc',
  'av. Atwater',
  'rue Guy',
  'rue Peel',
  'rue Drolet',
  'rue de Bullion',
  'rue Saint-Denis',
  'rue Napoléon',
  'rue Berri',
  'av. Christophe-Colomb',
  'rue Boyer',
];

/**
 * East-west streets, north → south (−Z is north). Index-matched to TUNING.grid.ewZ.
 *
 * The last three are the phase-1 downtown additions, appended because `ewZ` grew southward.
 * `boul. René-Lévesque` carries the artère and crosses the new quadrant. Sainte-Catherine sits
 * south of it rather than north as it does in the real city: it is the narrow shopping street
 * of the two, which makes it the better southern edge, and the arterial had to be the one that
 * crosses the quadrant rather than the one you cannot drive past.
 */
export const EW_NAMES = [
  'av. Laurier',
  'rue Gilford',
  'av. du Mont-Royal',
  'rue Marie-Anne',
  'rue Rachel',
  'rue Duluth',
  'av. des Pins',
  'rue Sherbrooke',
  'boul. René-Lévesque',
  'rue Sainte-Catherine',
];

/**
 * Commercial tenants. `kind` drives the sign look, `words` is what gets painted on the
 * fascia (line 1 large, line 2 small), `tint` is the board colour.
 */
export const SHOPS = [
  { name: 'Dépanneur Chez Momo', kind: 'dep', l1: 'DÉPANNEUR', l2: 'BIÈRE · VIN · FROMAGE', tint: 0xd8232a, ink: 0xfdf6e4 },
  { name: 'Dépanneur du Coin', kind: 'dep', l1: 'DÉPANNEUR', l2: 'BIÈRE FROIDE · LOTERIE', tint: 0x17603a, ink: 0xfdf6e4 },
  { name: 'Boulangerie Saint-Viateur', kind: 'food', l1: 'BOULANGERIE', l2: 'PAIN FRAIS DU JOUR', tint: 0xe8c33a, ink: 0x2a2118 },
  { name: 'Bagels Saint-Viateur', kind: 'food', l1: 'BAGELS', l2: 'CUITS AU FOUR À BOIS', tint: 0xf0efe6, ink: 0x1f2a44 },
  { name: 'Casse-croûte Chez Ti-Guy', kind: 'food', l1: 'CASSE-CROÛTE', l2: 'POUTINE · PATATES FRITES', tint: 0xd8232a, ink: 0xfdf6e4 },
  { name: 'Poutine Deluxe', kind: 'food', l1: 'POUTINE', l2: 'FRITES · GUEDILLE', tint: 0xf5a11a, ink: 0x2a2118 },
  { name: 'Smoked Meat Rachel', kind: 'food', l1: 'SMOKED MEAT', l2: 'SANDWICH · FUMÉ', tint: 0x8c1f1f, ink: 0xfdf6e4 },
  { name: 'Brasserie Le Vieux Plateau', kind: 'bar', l1: 'BRASSERIE', l2: 'BIÈRE EN FÛT · TERRASSE', tint: 0x1f3a52, ink: 0xf3e2b0 },
  { name: 'Taverne Napoléon', kind: 'bar', l1: 'TAVERNE', l2: 'OUVERT TARD', tint: 0x2a2118, ink: 0xf5a11a },
  { name: 'Tabagie Laurier', kind: 'dep', l1: 'TABAGIE', l2: 'JOURNAUX · LOTERIE', tint: 0x0f6b8c, ink: 0xfdf6e4 },
  { name: 'Buanderie Mile End', kind: 'service', l1: 'BUANDERIE', l2: 'LAVE · SÈCHE · PLIE', tint: 0x2f7fb5, ink: 0xfdf6e4 },
  { name: 'Quincaillerie Rachel', kind: 'service', l1: 'QUINCAILLERIE', l2: 'OUTILS · PEINTURE · CLÉS', tint: 0xc04a18, ink: 0xfdf6e4 },
  { name: 'Pharmacie Duluth', kind: 'service', l1: 'PHARMACIE', l2: 'ORDONNANCES', tint: 0x17603a, ink: 0xf0efe6 },
  { name: 'Fruiterie Marie-Anne', kind: 'food', l1: 'FRUITERIE', l2: 'FRUITS · LÉGUMES', tint: 0x4f7528, ink: 0xf7f3e0 },
  { name: 'Rôtisserie Saint-Denis', kind: 'food', l1: 'RÔTISSERIE', l2: 'POULET BBQ', tint: 0x8c1f1f, ink: 0xf5d24a },
  { name: 'Cordonnerie Gilford', kind: 'service', l1: 'CORDONNERIE', l2: 'CLÉS MINUTE', tint: 0x4a3728, ink: 0xf0efe6 },
  { name: 'Café Olimpico', kind: 'bar', l1: 'CAFÉ', l2: 'ESPRESSO · CANNOLI', tint: 0x1f3a52, ink: 0xf0efe6 },
  { name: 'Nettoyeur Boyer', kind: 'service', l1: 'NETTOYEUR', l2: 'À SEC · RETOUCHES', tint: 0x2f5f7a, ink: 0xf0efe6 },
  { name: 'Vélo Berri', kind: 'service', l1: 'VÉLO', l2: 'RÉPARATION · PIÈCES', tint: 0x0f6b5c, ink: 0xf7f3e0 },
  { name: 'Épicerie Christophe-Colomb', kind: 'food', l1: 'ÉPICERIE', l2: 'OUVERT 7 JOURS', tint: 0xc9493f, ink: 0xf7f3e0 },
  { name: 'Boucherie Drolet', kind: 'food', l1: 'BOUCHERIE', l2: 'VIANDES · CHARCUTERIE', tint: 0x8c1f1f, ink: 0xf0efe6 },
  { name: 'Friperie Duluth', kind: 'service', l1: 'FRIPERIE', l2: 'VÊTEMENTS USAGÉS', tint: 0x8a4a7a, ink: 0xf7f3e0 },
  { name: 'Crèmerie Mont-Royal', kind: 'food', l1: 'CRÈMERIE', l2: 'MOLLE · SUNDAE', tint: 0xf5a11a, ink: 0x2a2118 },
  { name: 'Poissonnerie Rachel', kind: 'food', l1: 'POISSONNERIE', l2: 'FRAIS DU JOUR', tint: 0x2f7fb5, ink: 0xf7f3e0 },
];

/** Short blade-sign words — the projecting perpendicular signs. */
export const BLADE_WORDS = [
  { t: 'BIÈRE', tint: 0xd8232a, ink: 0xfdf6e4 },
  { t: 'TABAGIE', tint: 0x0f6b8c, ink: 0xfdf6e4 },
  { t: 'POUTINE', tint: 0xf5a11a, ink: 0x2a2118 },
  { t: 'OUVERT', tint: 0x17603a, ink: 0xfdf6e4 },
  { t: 'DÉP.', tint: 0xd8232a, ink: 0xfdf6e4 },
  { t: 'CAFÉ', tint: 0x2a2118, ink: 0xf3e2b0 },
  { t: 'BAR', tint: 0x1f3a52, ink: 0xf5a11a },
  { t: 'PHARMA', tint: 0x17603a, ink: 0xf0efe6 },
  { t: 'LOTERIE', tint: 0x8a4a7a, ink: 0xf7f3e0 },
  { t: 'BAGELS', tint: 0xf0efe6, ink: 0x1f2a44 },
];

/** Ville de Montréal parking signs. Famously unreadable; ours are legible-ish nonsense. */
export const PARKING_SIGNS = [
  ['P', '9h À 17h', 'LUN À VEN', '2h MAX'],
  ['⛔', 'STATIONNEMENT', 'INTERDIT', 'MAR 13h-14h'],
  ['P', 'SAUF', 'PERMIS 42', 'RÉSIDENTS'],
  ['⛔', 'DÉNEIGEMENT', '1 DÉC - 1 AVR', '0h À 7h'],
];

/** Names attached to a landmark record but not to a shop. */
export const CIVIC = [
  { name: 'Église Saint-Jean-Baptiste', kind: 'church' },
  { name: 'Croix du Mont-Royal', kind: 'monument' },
  { name: 'Parc Napoléon', kind: 'park' },
];

/** Index-matched to TUNING.ruelles. */
export function ruelleName(i) {
  return ['Ruelle Napoléon', 'Ruelle Saint-Antoine', 'Ruelle Sainte-Cunégonde',
    'Ruelle des Bagels'][i] || 'Ruelle';
}
